import * as THREE from 'three/webgpu';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import { texture as textureNode, uv, vec2 } from 'three/tsl';
import { FuseRenderer } from '@/renderer';
import { WorldMask } from '@/xr/worldMask';

const UP = new THREE.Vector3(0, 1, 0);

export type ShotTvOptions = {
  /** Render target width in pixels; height follows the 16:9 screen. */
  width?: number;
  /** Screen width in metres. */
  screenWidth?: number;
  /** Colour the offscreen pass clears to (the sky behind the range). */
  clearColor?: THREE.ColorRepresentation;
};

export type ShotTvRenderOptions = {
  renderer: FuseRenderer;
  scene: THREE.Scene;
  camera: THREE.Camera;
  /** Objects to hide for the duration of the pass (the in-world UI). */
  hide?: THREE.Object3D[];
  /** Temporarily neutralised so the TV image has no punch-through holes. */
  worldMask?: WorldMask;
};

/**
 * Floating screen showing a chase-camera view of the ball in flight.
 *
 * Placed across the tee at eye level — the direction the player faces at
 * address, 90° off the target line — so it never sits in the ball path or the
 * downrange view. Lives in XR reference space alongside the other in-world UI.
 */
export class ShotTv {
  readonly object: THREE.Group;
  readonly target: THREE.RenderTarget;

  #screen: THREE.Mesh;
  #backing: THREE.Mesh;
  #clearColor: THREE.Color;
  #placed = false;
  /** Set once the offscreen pass has thrown; it is never retried after that. */
  #failed = false;

  constructor(options: ShotTvOptions = {}) {
    const width = options.width ?? 640;
    const height = Math.round(width * 9 / 16);
    this.#clearColor = new THREE.Color(options.clearColor ?? '#a8c6d8');

    this.target = new THREE.RenderTarget(width, height, {
      depthBuffer: true,
      // No MSAA: this is a small texture displayed at ~1 m, and the pass is
      // pure overhead on top of two eye buffers.
      samples: 0,
    });

    const screenWidth = options.screenWidth ?? 1.0;
    const screenHeight = screenWidth * 9 / 16;

    this.object = new THREE.Group();
    this.object.name = 'ShotTv';
    this.object.visible = false;
    this.object.userData.mrMaskExempt = true;

    // Dark surround so the picture reads as a screen against bright, denoised
    // passthrough rather than a floating rectangle of scenery.
    const bezel = 0.03;
    this.#backing = new THREE.Mesh(
      new THREE.PlaneGeometry(screenWidth + bezel * 2, screenHeight + bezel * 2),
      new THREE.MeshBasicMaterial({ color: 0x0b1414, fog: false, toneMapped: false })
    );
    this.#backing.position.z = -0.005;
    this.#backing.userData.mrMaskExempt = true;
    this.object.add(this.#backing);

    // toneMapped false: the scene was already tone mapped when it was drawn
    // into the target, and mapping it a second time washes the picture out.
    const screenMaterial = new MeshBasicNodeMaterial({ fog: false, toneMapped: false });
    // Explicit V flip. Render-target contents come out bottom-up on the WebGL
    // backend, so sampling with plain UVs shows the picture upside down — sky
    // at the bottom, mountains hanging down. Flipping here rather than via
    // texture.flipY/repeat keeps it independent of backend upload conventions.
    screenMaterial.colorNode = textureNode(this.target.texture, vec2(uv().x, uv().y.oneMinus()));
    this.#screen = new THREE.Mesh(
      new THREE.PlaneGeometry(screenWidth, screenHeight),
      screenMaterial
    );
    this.#screen.userData.mrMaskExempt = true;
    this.object.add(this.#screen);
  }

  /**
   * Across the tee at eye level, further out than the stats panel and facing
   * back at the tee. Same rotate-the-offset-by-yaw idiom as
   * ShotStatsPanel.placeAtTee; side is the handedness (1 = right-handed).
   */
  placeAtTee(teePosition: THREE.Vector3, yaw: number, side: 1 | -1 = 1) {
    const offset = new THREE.Vector3(-1.9 * side, 1.55, 0.55).applyAxisAngle(UP, yaw);
    this.object.position.copy(teePosition).add(offset);
    // Fixed orientation rather than a head billboard: a screen that swivels
    // while you turn to watch the ball is worse than one that stays put.
    this.object.rotation.y = yaw + side * Math.PI / 2;
    this.#placed = true;
  }

  get isPlaced(): boolean {
    return this.#placed;
  }

  get isAvailable(): boolean {
    return !this.#failed;
  }

  setVisible(visible: boolean) {
    // Never show an unplaced screen — it would hang at the reference-space origin
    this.object.visible = visible && this.#placed && !this.#failed;
  }

  /**
   * Draw the chase view into the target. Returns false if the pass failed, in
   * which case the TV disables itself permanently for the session — a broken
   * offscreen pass must never take the range down with it.
   */
  render(options: ShotTvRenderOptions): boolean {
    if (this.#failed) return false;

    const renderer: any = options.renderer.renderer;
    const xr = renderer.xr;
    const xrWasEnabled = !!xr?.enabled;
    const maskWasEnabled = options.worldMask ? options.worldMask.uEnabled.value : 0;

    const previousTarget = typeof renderer.getRenderTarget === 'function'
      ? renderer.getRenderTarget()
      : null;
    const previousClear = new THREE.Color();
    let previousAlpha = 0;
    try {
      renderer.getClearColor(previousClear);
      previousAlpha = renderer.getClearAlpha();
    } catch {
      previousClear.setHex(0x000000); // XR construction default
    }

    const hidden: THREE.Object3D[] = [];

    try {
      for (const object of options.hide ?? []) {
        if (object.visible) {
          object.visible = false;
          hidden.push(object);
        }
      }
      // The portal mask would carve passthrough holes into the picture; with
      // uEnabled 0 the mask term is 1 and the punch blend degenerates to opaque.
      options.worldMask?.setEnabled(false);
      // three swaps in xr.getCamera() whenever xr.isPresenting, which would
      // render the headset view into our target instead of the chase view.
      if (xr) xr.enabled = false;
      // The XR clear is (0,0,0,0) for passthrough compositing; alpha 0 here
      // would make the screen itself see-through.
      renderer.setClearColor(this.#clearColor, 1);
      renderer.setRenderTarget(this.target);
      renderer.render(options.scene, options.camera);
      return true;
    } catch (error) {
      console.error('ShotTv: offscreen pass failed, disabling the TV screen', error);
      this.#failed = true;
      this.object.visible = false;
      return false;
    } finally {
      renderer.setRenderTarget(previousTarget);
      renderer.setClearColor(previousClear, previousAlpha);
      if (xr) xr.enabled = xrWasEnabled;
      options.worldMask?.setEnabled(!!maskWasEnabled);
      for (const object of hidden) object.visible = true;
    }
  }

  dispose() {
    this.object.removeFromParent();
    this.#screen.geometry.dispose();
    (this.#screen.material as THREE.Material).dispose();
    this.#backing.geometry.dispose();
    (this.#backing.material as THREE.Material).dispose();
    this.target.dispose();
  }
}
